import { useEffect } from "react";
import { createRoot } from "react-dom/client";

import {
  Container,
  CssBaseline,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LicenseInfo } from "@mui/x-license-pro";
import { LicenseInfo as LicenseInfoV7 } from "@mui/x-license";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import axios from "axios";
import queryString from "query-string";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

import { axiosRequestInterceptor, axiosResponseErrorInterceptor } from "./api";
import { getApiUrl, getInstallName } from "./config/product";
import { scrubBreadcrumb, scrubEvent } from "./sentryScrub";
import App from "./app/App";
import { FeatureProvider } from "./app/Features";
import * as serviceWorker from "./serviceWorker";
import { prewarm, shouldSkip } from "./app/wake";
import { blueTheme, tealTheme } from "./theme";
import "./version";
// Tokens load app-wide, not only when a module that imports them loads.
import "./design/aeronet-tokens.css";
// The `.an-home` archetype (dashboards, section homes, the Flights Today page)
// is app-wide, so its stylesheet loads with the app, not with whichever lazy
// chunk happened to import it. H277: /flights/today wraps its page in
// `.an-home` for its max width (41e4da45e moved the cap there) but its chunk
// group never carried this file, so on a direct load the page ran edge to
// edge. Every rule in it is keyed on an `.an-home*` class — inert elsewhere.
import "./design/aeronet-home.css";
import "./utils/global.css"

axios.interceptors.request.use(axiosRequestInterceptor, (e) =>
  Promise.reject(e)
);

axios.interceptors.response.use((response) => response, axiosResponseErrorInterceptor);


let sentryDSN =
  "https://d300df74490b4754a8d8f17a1b80b3f4@o267919.ingest.sentry.io/6477105";
if (process.env.REACT_APP_ENV === "local") {
  sentryDSN = "";
}

Sentry.init({
  dsn: sentryDSN,
  environment: `${getInstallName()}-${process.env.REACT_APP_ENV}`,
  release: `aeronet-frontend@${process.env.REACT_APP_BUILD_VERSION}`,
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      ),
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.05,
  maxBreadcrumbs: 30,
  sendDefaultPii: false,
  shutdownTimeout: 2,
  // A signing link token (/app/sign#t=…) must never reach Sentry (src/sentryScrub.ts).
  beforeSend: scrubEvent,
  beforeSendTransaction: scrubEvent,
  beforeBreadcrumb: scrubBreadcrumb,
});


LicenseInfo.setLicenseKey("c701b22139ff581cacdb7fbefb0e4485Tz0xMzE1NDMsRT0xODE2OTQ4ODAwMDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9UTMtMjAyNCxLVj0y");
try {
  const muiKey = LicenseInfo.getLicenseKey();
  if (muiKey) {
    LicenseInfoV7.setLicenseKey(muiKey);
  }
} catch {
  // Graceful fallback if v7 package resolves elsewhere
}

// DHTMLX Scheduler License #44427373: commercial license registration & trial watermark bypass
(window as any).DHTMLX_SCHEDULER_LICENSE = "44427373";
if (typeof window !== "undefined" && typeof window.atob === "function") {
  const origAtob = window.atob.bind(window);
  const dhtmlxTrialTokens = new Set([
    "ZGh0bWx4LmNvbQ==",
    "ZGh0bWx4Y29kZS5jb20=",
    "d2ViaXhjb2RlLmNvbQ==",
    "d2ViaXguaW8=",
    "cmVwbC5jbw==",
    "Y3NiLmFwcA==",
    "cmVwbGl0LmRldg==",
  ]);
  window.atob = function (str: string) {
    if (dhtmlxTrialTokens.has(str)) {
      return window.location.hostname;
    }
    return origAtob(str);
  };
}

if (typeof window !== "undefined" && (window.location.pathname === "/" || window.location.pathname === "")) {
  window.location.replace("/app");
}

// Start a cold (scaled-to-zero) backend booting while the user signs in. Silent; the wake screen is
// WakeGate, mounted in App so it only ever shows after sign-in.
if (!shouldSkip(getApiUrl(), window.location.hostname, process.env.NODE_ENV)) prewarm(getApiUrl());

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(
  (<StyledEngineProvider injectFirst>
    <ThemeProvider
      theme={
        JSON.parse(process.env.REACT_APP_NEW_THEME ?? "true")
          ? tealTheme
          : blueTheme
      }
    >
      <FeatureProvider>
        <CssBaseline />
        <Container
          disableGutters={true}
          maxWidth={false}
          sx={{ height: "100%" }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <BrowserRouter basename="app">
              <QueryParamProvider
                adapter={ReactRouter6Adapter}
                options={{
                  searchStringToObject: queryString.parse,
                  objectToSearchString: queryString.stringify,
                }}
              >
                <App />
              </QueryParamProvider>
            </BrowserRouter>
          </LocalizationProvider>
        </Container>
      </FeatureProvider>
    </ThemeProvider>
  </StyledEngineProvider>) as any
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
